import * as RadixPopover from '@radix-ui/react-popover';
import { Column } from '@tanstack/react-table';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ChevronDownIcon from '../assets/icons/chevron_down.svg?react';
import SearchIcon from '../assets/icons/search.svg?react';
import { useLayoutTier } from '../hooks/useLayoutTier';
import { cn } from '../utils/className';
import { Checkbox } from './Checkbox';
import { MobileSearchableSelect } from './MobileSearchableSelect';
import {
  ClearFilterButton,
  FilterSearchRow,
  SortButtons,
} from './tables/columnHelpers';

/**
 * SearchableSelect: Standalone dropdown component with trigger button
 * InlineSearchableList: Filter-specific component used inside TableColumnFilter popovers
 */

export interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  selectedValues?: string[];
  onSelectionChange?: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  mode?: 'single' | 'multi';
  disabled?: boolean;
  /** Label for the mobile bottom sheet. Recommended for accessibility. */
  label?: string;
}

/**
 * Inline searchable list (no trigger/dropdown) - used inside popovers
 * Shows search input and options list directly
 *
 * Sorting is only supported via TanStack Table column prop - sorting is always a table column concern.
 */
export interface InlineSearchableListProps<TData = unknown> {
  options: SearchableSelectOption[];
  selectedValues?: string[];
  onSelectionChange?: (values: string[]) => void;
  placeholder?: string;
  mode?: 'single' | 'multi';
  className?: string;
  /** TanStack Table column - if provided, enables sorting via SortButtons */
  column?: Column<TData, unknown>;
  onClearFilter?: () => void;
  onClose?: () => void;
  hasFilterOrSort?: boolean;
}

export function InlineSearchableList<TData = unknown>({
  options,
  selectedValues = [],
  onSelectionChange,
  placeholder = 'Search...',
  mode = 'multi',
  className,
  column,
  onClearFilter,
  onClose,
  hasFilterOrSort,
}: InlineSearchableListProps<TData>) {
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const canSort = column?.getCanSort() ?? false;

  useEffect(() => {
    // Focus the search input when the component mounts
    searchInputRef.current?.focus();
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const handleToggleOption = (value: string) => {
    if (!onSelectionChange) return;

    if (mode === 'single') {
      onSelectionChange([value]);
    } else {
      const newSelection = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onSelectionChange(newSelection);
    }
  };

  const hasSelections = selectedValues.length > 0;

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (hasFilterOrSort ?? hasSelections) {
        onClearFilter?.();
        onClose?.();
      }
    },
    [hasFilterOrSort, hasSelections, onClearFilter, onClose]
  );

  return (
    <div className={cn('w-full min-w-50', className)}>
      {/* Search input - p-3 (12px) like Figma */}
      <FilterSearchRow
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={placeholder}
        inputRef={searchInputRef}
      />

      {/* Options list - p-2 (8px) for items, gap-3 (12px) for checkbox+label */}
      <div className='max-h-50 overflow-y-auto'>
        {filteredOptions.length === 0 ? (
          <div className='size-p2 text-secondary p-2 text-center'>
            No results found
          </div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <button
                key={option.value}
                type='button'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggleOption(option.value);
                }}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-3 p-2',
                  'transition-colors outline-none',
                  'focus-visible:ring-1',
                  'focus-visible:ring-blue-500',
                  'focus-visible:ring-inset',
                  isSelected
                    ? 'bg-surface-focus-support'
                    : 'hover:bg-surface-focus-support'
                )}
              >
                {mode === 'multi' && (
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggleOption(option.value)}
                    onClick={(e) => e.stopPropagation()}
                    tabIndex={-1}
                  />
                )}
                <div
                  className='flex min-w-0 flex-1 items-center justify-between'
                >
                  <span className='size-p2 text-primary truncate'>
                    {option.label}
                  </span>
                  {option.description && (
                    <span className='size-caption text-tertiary truncate'>
                      {option.description}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Sort and Clear Actions */}
      {(canSort || onClearFilter) && (
        <div className='border-border-secondary border-t'>
          {canSort && column && (
            <SortButtons column={column} onClose={onClose} />
          )}

          {onClearFilter && (
            <ClearFilterButton
              disabled={!(hasFilterOrSort ?? hasSelections)}
              onClick={handleClear}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function SearchableSelect({
  options,
  selectedValues = [],
  onSelectionChange,
  placeholder = 'Select...',
  className,
  mode = 'multi',
  disabled = false,
  label,
}: SearchableSelectProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number>();
  const dropdownSearchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { isTouch } = useLayoutTier();

  useEffect(() => {
    if (!open || !triggerRef.current) {
      return;
    }

    const updateWidth = () => {
      setTriggerWidth(triggerRef.current?.offsetWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(triggerRef.current);
    return () => observer.disconnect();
  }, [open]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const displayText = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const selected = options.find((opt) => opt.value === selectedValues[0]);
      return selected?.label || placeholder;
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, options, placeholder]);

  // Render MobileSearchableSelect on mobile devices for better UX
  if (isTouch) {
    return (
      <MobileSearchableSelect
        options={options}
        selectedValues={selectedValues}
        onSelectionChange={onSelectionChange}
        placeholder={placeholder}
        className={className}
        mode={mode}
        disabled={disabled}
        label={label}
      />
    );
  }

  const handleToggleOption = (value: string) => {
    if (!onSelectionChange) return;

    if (mode === 'single') {
      // In single mode, always replace with the new selection and close
      onSelectionChange([value]);
      setOpen(false);
      setSearchQuery('');
    } else {
      // In multi mode, toggle the selection
      const newSelection = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onSelectionChange(newSelection);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setSearchQuery('');
  };

  // Determine if there are selected values to apply active styling
  const hasSelectedValues = selectedValues.length > 0;

  return (
    <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
      <RadixPopover.Trigger asChild>
        <button
          ref={triggerRef}
          type='button'
          disabled={disabled}
          className={cn(
            `flex min-h-11 w-full items-center justify-between gap-2 rounded
            border px-2 py-1.5 md:min-h-0`,
            'size-p2 font-regular',
            'transition-colors outline-none',
            'focus-visible:border-border-focus',
            // Disabled state
            disabled && 'cursor-not-allowed opacity-50',
            // Default cursor when not disabled
            !disabled && 'cursor-pointer',
            // Default inactive state (can be overridden by className prop)
            !className &&
              !hasSelectedValues &&
              'border-border-primary bg-surface-primary text-tertiary',
            // Default active state (can be overridden by className prop)
            !className &&
              hasSelectedValues &&
              'border-border-focus bg-surface-primary text-focus',
            className
          )}
        >
          <span className='truncate'>{displayText}</span>
          <span
            className='text-secondary flex h-4 w-4 shrink-0 items-center
              justify-center [&_path]:fill-current [&_svg]:fill-current'
          >
            <ChevronDownIcon />
          </span>
        </button>
      </RadixPopover.Trigger>

      <RadixPopover.Portal>
        <RadixPopover.Content
          className='border-border-secondary bg-surface-primary z-9999 rounded
            border shadow-lg'
          style={{ minWidth: triggerWidth }}
          sideOffset={4}
          align='start'
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (!isTouch) {
              dropdownSearchRef.current?.focus();
            }
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className='w-full'>
            <div className='border-border-secondary border-b py-2'>
              <div
                className='bg-surface-primary flex items-center gap-2 rounded
                  px-2 py-1.5'
              >
                <span
                  className='text-primary flex h-4 w-4 shrink-0 items-center
                    justify-center [&_path]:fill-current [&_svg]:fill-current'
                >
                  <SearchIcon />
                </span>
                <input
                  ref={dropdownSearchRef}
                  type='text'
                  placeholder='Search...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='size-p2 text-primary placeholder:text-disabled
                    flex-1 bg-transparent outline-none'
                />
              </div>
            </div>

            <div role='listbox' className='max-h-50 overflow-y-auto'>
              {filteredOptions.length === 0 ? (
                <div className='size-p2 text-secondary p-2 text-center'>
                  No results found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type='button'
                      role='option'
                      aria-selected={isSelected}
                      onClick={() => handleToggleOption(option.value)}
                      className={cn(
                        `flex w-full cursor-pointer items-center gap-3 p-2
                          transition-colors outline-none`,
                        'focus-visible:ring-1',
                        'focus-visible:ring-blue-500',
                        'focus-visible:ring-inset',
                        isSelected
                          ? 'bg-surface-focus-support'
                          : 'hover:bg-surface-focus-support'
                      )}
                    >
                      {mode === 'multi' && (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() =>
                            handleToggleOption(option.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                          tabIndex={-1}
                        />
                      )}
                      <div
                        className='flex min-w-0 flex-1 items-center
                          justify-between'
                      >
                        <span className='size-p2 text-primary truncate'>
                          {option.label}
                        </span>
                        {option.description && (
                          <span className='size-caption text-tertiary truncate'>
                            {option.description}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
